import { Text } from "@mantine/core";
import { useMemo } from "react";
import useSWR from "swr";
import { BillingTenantApi } from "../../apis";

// Single shared fetch of the billing tenants aggregate (same source the tenants
// list + detail header use), built into an id -> name map. SWR dedupes by key so
// every TenantName instance shares one request. Read-only — no mutation.
export function useTenantNameMap(): Map<string, string> {
  const { data } = useSWR(
    "swr.billing.tenants.namemap",
    () => BillingTenantApi.listBillingTenants({ limit: 100 }),
    { revalidateOnFocus: false }
  );
  return useMemo(() => {
    const m = new Map<string, string>();
    (data?.rows ?? []).forEach((t) => { if (t.name) m.set(t.id, t.name); });
    return m;
  }, [data]);
}

// Resolves a tenantId to its name, falling back to a short mono id while loading
// or when unresolved (e.g. tenant outside the first 100).
export function TenantName({ id }: { id: string }) {
  const map = useTenantNameMap();
  const name = id ? map.get(id) : undefined;
  if (!name) {
    return <Text size="sm" c="dimmed" ff="monospace" title={id}>{id ? `${id.slice(0, 8)}…` : "—"}</Text>;
  }
  return <Text size="sm">{name}</Text>;
}
